<template>
  <div class="sudoku-game">
    <!-- 游戏头部 -->
    <div class="sudoku-header">
      <div class="header-left">
        <h2>{{ t('games.sudoku.title') }}</h2>
        <p class="difficulty-label">{{ difficultyLabel }}</p>
      </div>
      <div class="header-right">
        <div class="game-stats">
          <div class="stat-item">
            <span class="stat-label">{{ t('game.hud.timer') }}</span>
            <span class="stat-value">{{ formatTime(elapsedTime) }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{{ t('game.hud.mistakes') }}</span>
            <span class="stat-value" :class="{ 'stat-danger': mistakes >= 3 }">{{ mistakes }}/3</span>
          </div>
        </div>
        <button class="btn-secondary btn-exit" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </div>

    <!-- 游戏内容 -->
    <div class="sudoku-main">
      <!-- 难度选择模式 -->
      <div v-if="gameState === 'selecting'" class="difficulty-selection">
        <h3>{{ t('games.sudoku.selectDifficulty') }}</h3>
        <div class="difficulty-options">
          <button
            v-for="difficulty in difficulties"
            :key="difficulty"
            class="difficulty-btn"
            @click="startGame(difficulty)"
          >
            <span class="difficulty-name">{{ getDifficultyName(difficulty) }}</span>
            <span class="difficulty-desc">{{ getDifficultyDesc(difficulty) }}</span>
          </button>
        </div>
      </div>

      <!-- 游戏进行中 -->
      <div v-else-if="gameState === 'playing'" class="game-content">
        <!-- 棋盘区域 -->
        <div class="board-area">
          <SudokuBoard
            :board="board"
            :solution="solution"
            :selected-cell="selectedCell"
            @select-cell="selectCell"
            @update-cell="updateCell"
          />
        </div>

        <!-- 控制面板 -->
        <div class="control-panel">
          <div class="controls-row">
            <button
              v-for="num in 9"
              :key="num"
              class="num-btn"
              :disabled="!selectedCell || !canInput(num)"
              @click="fillNumber(num)"
            >
              {{ num }}
            </button>
            <button class="num-btn btn-delete" :disabled="!selectedCell" @click="clearCell">
              {{ t('buttons.clear') }}
            </button>
          </div>
          <div class="controls-row">
            <button class="btn-primary btn-hint" @click="getHint" :disabled="hintsUsed >= 3">
              {{ t('buttons.hint') }} ({{ 3 - hintsUsed }})
            </button>
            <button class="btn-primary btn-check" @click="checkBoard">
              {{ t('buttons.check') }}
            </button>
            <button class="btn-secondary btn-restart" @click="restartGame">
              {{ t('buttons.restart') }}
            </button>
          </div>
        </div>

        <!-- 信息提示 -->
        <div v-if="message" class="message" :class="messageType">
          {{ message }}
        </div>
      </div>

      <!-- 游戏结束 -->
      <div v-else-if="gameState === 'won'" class="game-result">
        <div class="result-card">
          <h3>🎉 {{ t('games.sudoku.congratulations') }}</h3>
          <div class="result-stats">
            <p><strong>{{ t('game.hud.timer') }}:</strong> {{ formatTime(elapsedTime) }}</p>
            <p><strong>{{ t('game.hud.mistakes') }}:</strong> {{ mistakes }}/3</p>
            <p v-if="score > 0"><strong>{{ t('game.hud.score') }}:</strong> {{ score }}</p>
          </div>
          <div class="result-actions">
            <button class="btn-primary" @click="() => { gameState = 'selecting'; }">
              {{ t('buttons.playAgain') }}
            </button>
            <button class="btn-secondary" @click="goHome">
              {{ t('buttons.backHome') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 游戏失败 -->
      <div v-else-if="gameState === 'lost'" class="game-result">
        <div class="result-card result-lost">
          <h3>😢 {{ t('games.sudoku.gameOver') }}</h3>
          <p>{{ t('games.sudoku.tooManyMistakes') }}</p>
          <div class="result-actions">
            <button class="btn-primary" @click="() => { gameState = 'selecting'; }">
              {{ t('buttons.tryAgain') }}
            </button>
            <button class="btn-secondary" @click="goHome">
              {{ t('buttons.backHome') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from '@/utils/i18n'
import SudokuBoard from './SudokuBoard.vue'
import * as sudokuLib from 'sudoku'

const { t } = useI18n()

// 游戏状态
const gameState = ref('selecting') // selecting, playing, won, lost
const board = ref([])
const originalBoard = ref([])
const solution = ref([])
const selectedCell = ref(null)
const currentDifficulty = ref('medium')

// 游戏数据
const elapsedTime = ref(0)
const mistakes = ref(0)
const hintsUsed = ref(0)
const message = ref('')
const messageType = ref('')

// 计时器
let timerInterval = null

// 难度配置
const difficulties = ['easy', 'medium', 'hard']

const difficultyConfig = {
  easy: { givens: 40, label: t('games.sudoku.easy'), desc: '40个数字' },
  medium: { givens: 30, label: t('games.sudoku.medium'), desc: '30个数字' },
  hard: { givens: 20, label: t('games.sudoku.hard'), desc: '20个数字' }
}

// 计算属性
const difficultyLabel = computed(() => {
  return difficultyConfig[currentDifficulty.value]?.label || ''
})

const score = computed(() => {
  if (gameState.value !== 'won') return 0

  const baseScore = currentDifficulty.value === 'easy' ? 100 : currentDifficulty.value === 'medium' ? 300 : 600
  const timeBonus = Math.max(0, 600 - elapsedTime.value) // 最多10分钟减分
  const mistakeDeduction = mistakes.value * 50
  const hintDeduction = hintsUsed.value * 30

  return Math.max(0, baseScore + Math.floor(timeBonus / 10) - mistakeDeduction - hintDeduction)
})

// 预设的数独谜题（备选方案）
const getPresetPuzzle = (difficulty) => {
  const puzzles = {
    easy: {
      puzzle: '53..7....6..195....98....6.8...6.34..4......52...7.1.9....9....4.6..39.7..3..',
      solution: '534678912672195348198342567859761423426853791713924856961537284287419635345286179'
    },
    medium: {
      puzzle: '..3..6.4.1...4..5.......9.9.5.....6.....28.......7.7......2.1...7..8.6..9......',
      solution: '213985647192347856865741923956183742431726589728459361389514276574628931641832795'
    },
    hard: {
      puzzle: '2...8.3...6..7..1.9.5..7...8.5.1.7..5.4.6.8.1.9.5.8...2..3.7.1..9.4...5.4...9.7..8',
      solution: '247189365638574129195263784826751743593482618417936852381627495759318246264895137'
    }
  }
  return puzzles[difficulty]
}

// 开始游戏
const startGame = (difficulty) => {
  try {
    currentDifficulty.value = difficulty
    const config = difficultyConfig[difficulty]

    // 生成新谜题
    let puzzle, solved

    try {
      // 尝试使用 sudoku 库生成
      puzzle = sudokuLib.generate(config.givens)
      solved = sudokuLib.solve(puzzle)
      console.log('Generated puzzle with sudoku library')
    } catch (e) {
      // 失败时使用预设谜题
      console.warn('Sudoku library failed, using preset puzzle:', e)
      const preset = getPresetPuzzle(difficulty)
      puzzle = preset.puzzle
      solved = preset.solution
    }

    if (!puzzle || !solved) {
      showMessage('Failed to load puzzle', 'error')
      return
    }

    const boardArray = puzzle.split('').map(cell => {
      if (cell === '.') return null
      return parseInt(cell)
    })

    const solutionArray = solved.split('').map(cell => parseInt(cell))

    board.value = boardArray.map(v => ({ value: v, isOriginal: v !== null }))
    originalBoard.value = JSON.parse(JSON.stringify(board.value))
    solution.value = solutionArray

    selectedCell.value = null
    elapsedTime.value = 0
    mistakes.value = 0
    hintsUsed.value = 0
    message.value = ''
    gameState.value = 'playing'

    // 启动计时器
    if (timerInterval) clearInterval(timerInterval)
    timerInterval = setInterval(() => {
      elapsedTime.value++
    }, 1000)
  } catch (error) {
    console.error('Error starting sudoku game:', error)
    showMessage('Error starting game', 'error')
  }
}

// 选择格子
const selectCell = (index) => {
  const boardSize = 81
  if (index >= 0 && index < boardSize) {
    selectedCell.value = index
  }
}

// 更新格子（来自键盘输入）
const updateCell = (index, value) => {
  if (!board.value[index] || board.value[index].isOriginal) return
  board.value[index].value = value
}

// 填充数字
const fillNumber = (num) => {
  if (!selectedCell.value && selectedCell.value !== 0) return

  const cell = board.value[selectedCell.value]
  if (cell && !cell.isOriginal) {
    cell.value = num

    // 检查是否违反规则
    if (!isValidPlacement(selectedCell.value, num)) {
      showMessage(t('games.sudoku.invalidPlacement'), 'error')
      cell.value = null
      mistakes.value++

      if (mistakes.value >= 3) {
        gameState.value = 'lost'
        if (timerInterval) clearInterval(timerInterval)
      }
    }
  }
}

// 清除格子
const clearCell = () => {
  if (!selectedCell.value && selectedCell.value !== 0) return

  const cell = board.value[selectedCell.value]
  if (cell && !cell.isOriginal) {
    cell.value = null
  }
}

// 验证放置是否有效
const isValidPlacement = (index, num) => {
  const row = Math.floor(index / 9)
  const col = index % 9

  // 检查行
  for (let c = 0; c < 9; c++) {
    const cellIndex = row * 9 + c
    if (cellIndex !== index && board.value[cellIndex]?.value === num) {
      return false
    }
  }

  // 检查列
  for (let r = 0; r < 9; r++) {
    const cellIndex = r * 9 + col
    if (cellIndex !== index && board.value[cellIndex]?.value === num) {
      return false
    }
  }

  // 检查 3x3 方块
  const boxRow = Math.floor(row / 3) * 3
  const boxCol = Math.floor(col / 3) * 3
  for (let r = boxRow; r < boxRow + 3; r++) {
    for (let c = boxCol; c < boxCol + 3; c++) {
      const cellIndex = r * 9 + c
      if (cellIndex !== index && board.value[cellIndex]?.value === num) {
        return false
      }
    }
  }

  return true
}

// 检查棋盘
const checkBoard = () => {
  const isFilled = board.value.every(cell => cell.value !== null)

  if (!isFilled) {
    showMessage(t('games.sudoku.notComplete'), 'warning')
    return
  }

  // 验证棋盘
  const isValid = board.value.every((cell, index) => {
    return cell.value === solution.value[index]
  })

  if (isValid) {
    gameState.value = 'won'
    if (timerInterval) clearInterval(timerInterval)
    showMessage(t('games.sudoku.solved'), 'success')
  } else {
    showMessage(t('games.sudoku.incorrectSolution'), 'error')
  }
}

// 获得提示
const getHint = () => {
  if (hintsUsed.value >= 3) {
    showMessage(t('games.sudoku.noMoreHints'), 'warning')
    return
  }

  // 找一个空格子并填入提示
  const emptyIndices = board.value
    .map((cell, i) => !cell.isOriginal && cell.value === null ? i : -1)
    .filter(i => i >= 0)

  if (emptyIndices.length === 0) {
    showMessage(t('games.sudoku.noEmptyCells'), 'warning')
    return
  }

  const randomIndex = emptyIndices[Math.floor(Math.random() * emptyIndices.length)]
  const cell = board.value[randomIndex]
  cell.value = solution.value[randomIndex]

  hintsUsed.value++
  showMessage(t('games.sudoku.hintProvided'), 'info')
}

// 重新开始
const restartGame = () => {
  board.value = JSON.parse(JSON.stringify(originalBoard.value))
  selectedCell.value = null
  elapsedTime.value = 0
  mistakes.value = 0
  hintsUsed.value = 0
  message.value = ''
  gameState.value = 'playing'

  if (timerInterval) clearInterval(timerInterval)
  timerInterval = setInterval(() => {
    elapsedTime.value++
  }, 1000)
}

// 返回主页
const goHome = () => {
  if (timerInterval) clearInterval(timerInterval)

  // 如果在游戏中（playing/won/lost），回到难度选择
  if (gameState.value !== 'selecting') {
    gameState.value = 'selecting'
  } else {
    // 如果在难度选择页，回到首页
    window.location.hash = '#/'
  }
}

// 辅助函数
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${mins}:${String(secs).padStart(2, '0')}`
}

const getDifficultyName = (difficulty) => {
  const names = {
    easy: t('games.sudoku.easy'),
    medium: t('games.sudoku.medium'),
    hard: t('games.sudoku.hard')
  }
  return names[difficulty] || difficulty
}

const getDifficultyDesc = (difficulty) => {
  const descs = {
    easy: t('games.sudoku.easyDesc'),
    medium: t('games.sudoku.mediumDesc'),
    hard: t('games.sudoku.hardDesc')
  }
  return descs[difficulty] || ''
}

const showMessage = (text, type) => {
  message.value = text
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 3000)
}

const canInput = (num) => {
  if (!selectedCell.value && selectedCell.value !== 0) return false

  const cell = board.value[selectedCell.value]
  return cell && !cell.isOriginal
}

// 生命周期
onMounted(() => {
  // 初始化时显示难度选择
})

onBeforeUnmount(() => {
  if (timerInterval) clearInterval(timerInterval)
})
</script>

<style scoped>
.sudoku-game {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
  padding: 24px;
  background-color: var(--color-bg-light, #FAFBFC);
  gap: 20px;
}

/* 头部 */
.sudoku-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  background: var(--color-bg-card);
  border-radius: 12px;
  border: 1px solid var(--color-border, #E5E7EB);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.05));
}

.header-left h2 {
  margin: 0 0 4px 0;
  font-size: 24px;
  color: var(--color-text-primary, #1F2937);
}

.difficulty-label {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary, #6B7280);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.game-stats {
  display: flex;
  gap: 16px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-label {
  font-size: 12px;
  color: var(--color-text-secondary, #6B7280);
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: var(--color-primary-orange, #FF6B35);
}

.stat-danger {
  color: var(--color-danger, #EF4444);
}

.btn-exit {
  padding: 8px 16px;
}

/* 主内容区 */
.sudoku-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 难度选择 */
.difficulty-selection {
  text-align: center;
}

.difficulty-selection h3 {
  margin-top: 0;
  margin-bottom: 32px;
  font-size: 28px;
  color: var(--color-text-primary, #1F2937);
}

.difficulty-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  max-width: 700px;
}

.difficulty-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 20px;
  background: var(--color-bg-card);
  border: 2px solid var(--color-border, #E5E7EB);
  border-radius: 12px;
  cursor: pointer;
  transition: all var(--transition-base, 0.3s) var(--ease-in-out, ease-in-out);
  font-family: inherit;
}

.difficulty-btn:hover {
  border-color: var(--color-primary-orange, #FF6B35);
  background: rgba(255, 107, 53, 0.05);
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1));
}

.difficulty-name {
  font-weight: 600;
  font-size: 18px;
  color: var(--color-text-primary, #1F2937);
}

.difficulty-desc {
  font-size: 14px;
  color: var(--color-text-secondary, #6B7280);
}

/* 游戏内容 */
.game-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.board-area {
  background: var(--color-bg-card);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--color-border, #E5E7EB);
  box-shadow: var(--shadow-md, 0 4px 6px rgba(0,0,0,0.1));
}

/* 控制面板 */
.control-panel {
  width: 100%;
  max-width: 600px;
  background: var(--color-bg-card);
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--color-border, #E5E7EB);
}

.controls-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.controls-row:first-child {
  /* 数字按钮行 */
}

.controls-row:last-child {
  /* 功能按钮行 */
  margin-bottom: 0;
}

.num-btn {
  flex: 1;
  min-width: 40px;
  padding: 10px;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border, #E5E7EB);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base, 0.3s);
  font-size: 14px;
}

.controls-row:first-child .num-btn {
  flex-basis: calc(20% - 7px);
}

.num-btn:hover:not(:disabled) {
  border-color: var(--color-primary-orange, #FF6B35);
  background: rgba(255, 107, 53, 0.05);
}

.num-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.num-btn.btn-delete {
  background: #FEE2E2;
  border-color: var(--color-danger, #EF4444);
  color: var(--color-danger, #EF4444);
}

.btn-hint,
.btn-check,
.btn-restart {
  padding: 10px 12px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all var(--transition-base, 0.3s);
}

.btn-hint {
  background: var(--color-primary-orange, #FF6B35);
  color: white;
  flex: 1;
}

.btn-hint:hover:not(:disabled) {
  background: #FF5B1F;
}

.btn-hint:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-check {
  background: var(--color-success, #10B981);
  color: white;
  flex: 1;
  margin-left: 8px;
}

.btn-check:hover {
  background: #059669;
}

.btn-restart {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border, #E5E7EB);
  color: var(--color-text-primary, #1F2937);
  flex: 1;
  margin-left: 8px;
}

.btn-restart:hover {
  border-color: var(--color-primary-orange, #FF6B35);
  color: var(--color-primary-orange, #FF6B35);
}

/* 信息提示 */
.message {
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 500;
  max-width: 400px;
  animation: slideInUp 0.3s ease;
}

.message.success {
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #BBEAD5;
}

.message.error {
  background: #FEE2E2;
  color: #991B1B;
  border: 1px solid #FCA5A5;
}

.message.warning {
  background: #FEF3C7;
  color: #78350F;
  border: 1px solid #FCD34D;
}

.message.info {
  background: #DBEAFE;
  color: #1E40AF;
  border: 1px solid #93C5FD;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 游戏结果 */
.game-result {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.result-card {
  background: var(--color-bg-card);
  padding: 40px;
  border-radius: 16px;
  border: 2px solid var(--color-primary-orange, #FF6B35);
  box-shadow: var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1));
  text-align: center;
  max-width: 400px;
}

.result-card h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 28px;
  color: var(--color-text-primary, #1F2937);
}

.result-card.result-lost {
  border-color: var(--color-danger, #EF4444);
}

.result-card.result-lost h3 {
  color: var(--color-danger, #EF4444);
}

.result-stats {
  margin: 20px 0;
  padding: 20px;
  background: var(--color-bg-light, #F9FAFB);
  border-radius: 8px;
  text-align: left;
}

.result-stats p {
  margin: 8px 0;
  color: var(--color-text-primary, #1F2937);
}

.result-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 20px;
}

.result-actions button {
  padding: 12px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all var(--transition-base, 0.3s);
}

.btn-secondary {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border, #E5E7EB);
  color: var(--color-text-primary, #344054);
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
}

.btn-secondary:hover {
  border-color: var(--color-primary-orange, #FF6B35);
  color: var(--color-primary-orange, #FF6B35);
}

/* 响应式 */
@media (max-width: 768px) {
  .sudoku-game {
    padding: 12px;
    gap: 12px;
  }

  .sudoku-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    width: 100%;
    flex-direction: column;
  }

  .game-stats {
    width: 100%;
    justify-content: space-between;
  }

  .difficulty-options {
    grid-template-columns: 1fr;
  }

  .controls-row {
    grid-template-columns: repeat(5, 1fr);
  }

  .result-actions {
    grid-template-columns: 1fr;
  }
}
</style>
